import React, { useState } from 'react';
import { Typography } from '@mui/material';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import { sendPasswordResetEmail } from 'src/utils/firebase';
import Link from 'src/components/ui/Link';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import Icon from 'src/components/ui/Icon';
import useAppRouter from 'src/hooks/useAppRouter';
import { routePathLogin } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';

const MobileButton = styledComponent(Button)`
    width: 100%;
    height: 3rem;
`;

type FormValues = {
    email: string;
};

const ForgotPasswordForm: React.FC = () => {
    const { t } = useTranslation(['common']);

    const router = useAppRouter();

    const mobileView = Boolean(router.query.mobileView);

    const emailProps = router.query.emailProps ? String(router.query.emailProps) : undefined;

    const [emailMsgSentTo, setEmailMsgSentTo] = useState<string>('');

    const yupSchema = Yup.object().shape({
        email: Yup.string().email(t('common:errors.invalidEmail')).required(t('common:errors.required')),
    });

    const resetPwd = async (values: FormValues) => {
        try {
            await sendPasswordResetEmail(values.email, {
                url: String(process.env.NEXT_PUBLIC_SITE_URL),
            });
        } catch (error: unknown) {
            // Do as if it worked anyways (security practice),
            // otherwise users can use this to check if an email is registered
        }
        setEmailMsgSentTo(values.email);
    };

    const displayMobileSentMessage = mobileView && emailMsgSentTo !== '';

    return (
        <>
            {!displayMobileSentMessage && (
                <Formik
                    initialValues={{
                        email: emailProps ?? '',
                    }}
                    validationSchema={yupSchema}
                    onSubmit={resetPwd}
                >
                    <Form>
                        <Box mb={2}>
                            <Typography variant="h1">{t('common:forgotPassword.title')}</Typography>
                        </Box>
                        <Box mb={7}>
                            <Typography>{t('common:forgotPassword.description')}</Typography>
                        </Box>
                        <TextField
                            name="email"
                            label={!mobileView ? t('common:forgotPassword.email') : ''}
                            type="email"
                            inputProps={mobileView ? { style: { fontSize: '1rem' } } : undefined} // Browsers will auto-zoom for inputs with font-size less than 16
                        />
                        {emailMsgSentTo !== '' && (
                            <>
                                <Typography variant="body1">{t('common:forgotPassword.successMsg')}</Typography>
                                <Link href={routePathLogin}>{t('common:forgotPassword.goToLogin')}</Link>
                            </>
                        )}
                        {emailMsgSentTo === '' && !mobileView && (
                            <Button type="submit">{t('common:forgotPassword.button')}</Button>
                        )}
                        {emailMsgSentTo === '' && mobileView && (
                            <MobileButton type="submit">{t('common:forgotPassword.button')}</MobileButton>
                        )}
                    </Form>
                </Formik>
            )}
            {displayMobileSentMessage && (
                <>
                    <Icon name="CheckEmailIcon" />
                    <Box mb={2}>
                        <Typography variant="h1">{t('common:forgotPassword.checkEmails')}</Typography>
                    </Box>
                    <Box mb={7}>
                        <Typography>{t('common:forgotPassword.linkHasBeenSent', { email: emailMsgSentTo })}</Typography>
                    </Box>
                </>
            )}
        </>
    );
};

export default ForgotPasswordForm;
